// IE Hub - Edit Company Dialog Component
// Allows editing a company's account number

import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Backdrop,
  useMediaQuery,
  useTheme,
  Slide,
} from "../ui";
import CloseIcon from "@mui/icons-material/Close";
import type { Company } from "@/pages/private/dashboard/SharePage";
import { debtEndpoints, sharePageEndpoints } from "@/shared/api/endpoints";
import {
  ApiResponse,
  CustomerSharingResponse,
  PaymentOfferRecord,
} from "@/shared/api/types";
import { httpGet, httpPut } from "@/shared/api/http";
import { useAlert } from "../app/providers/AlertProvider";
import { UI_MESSAGES } from "../constants/ui";
import React from "react";
import { DialogTitle } from "@ui";
import { ThemeColours } from "@/clients/acme/config/colours";
import SpecialTextField from "@/ui/fields/SpecialTextField";
import { useReferenceIdHelp } from "@/components/referenceId";
import { InputAdornment, MenuItem, Select, TextField } from "@mui/material";
import { DebtsService } from "@/shared/api/services";
import { ordinal } from "@/shared/share/sharePageUtils";

interface EditCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  company: Company;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
  setOpenDeleteDialog: (boolean) => void;
  companies?: Company[];
}

const Transition = React.forwardRef<
  unknown,
  { in?: boolean; children: React.ReactElement }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref as React.Ref<unknown>} {...props} />;
});

const EditCompanyDialog: React.FC<EditCompanyDialogProps> = ({
  open,
  onClose,
  company,
  setCompanies,
  setOpenDeleteDialog,
  companies,
}) => {
  const { showAlert } = useAlert();
  const [paymentOfferRecord, setPaymentOfferRecord] =
    useState<PaymentOfferRecord>({} as PaymentOfferRecord);

  const [coveringNotes, setCoveringNotes] = useState("");

  const [editedAccountNumber, setEditedAccountNumber] = useState(
    company.reference
  );
  const [noReferenceId, setNoReferenceId] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    setEditedAccountNumber(company.reference ?? "");
    setCoveringNotes(company.raw?.notes || "");
    if (company.reference == customerData.emailAddress) {
      setNoReferenceId(true);
      setEditedAccountNumber("");
    } else {
      setNoReferenceId(false);
    }
  }, [company, open]);

  useEffect(() => {
    if (!open || company.isFp) {
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    const lenderName = company.raw?.lenderName;
    const accountNumber = company.raw?.accountNumber;
    const customerId = customerData.customerId;

    async function main() {
      const response = await httpGet<ApiResponse>(
        debtEndpoints.paymentOffer.getSingle(
          customerId,
          lenderName as string,
          accountNumber as string
        )
      );

      const data: any = response?.data;

      if (data.length > 0) {
        setPaymentOfferRecord(data[0]);
      } else {
        setPaymentOfferRecord({} as PaymentOfferRecord);
      }
    }

    main();
  }, [company, open]);

  const [isSaving, setIsSaving] = useState(false);

  const {
    helpDialog: editReferenceHelpDialog,
    helpLinkText: editReferenceHelpLinkText,
    noValueCheckboxLabel: editNoValueCheckboxLabel,
    label: editReferenceLabel,
  } = useReferenceIdHelp({ companyName: company?.name });

  const onDelete = () => {
    onClose();
    setOpenDeleteDialog(true);
  };

  const handleSave = async () => {
    if (company.isFp && coveringNotes.length == 0) {
      showAlert({
        severity: "warning",
        message: `Please enter a covering note.`,
        autoHide: true,
        duration: 5000,
      });
      return;
    }

    if (companies) {
      for (const c of companies) {
        if (
          c.reference == editedAccountNumber &&
          c.raw?.id != company.raw?.id
        ) {
          showAlert({
            severity: "warning",
            message: `A company with the same reference number already exists`,
            autoHide: true,
            duration: 2000,
          });

          return;
        }
      }
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    if (
      !noReferenceId &&
      (editedAccountNumber.length == 0 || editedAccountNumber.includes("'"))
    ) {
      showAlert({
        severity: "warning",
        message: "Please enter a valid reference number.",
        autoHide: true,
        duration: 2500,
      });
      return;
    }

    let newAccNumber = editedAccountNumber;

    if (noReferenceId) {
      newAccNumber = customerData.emailAddress;
    }

    setIsSaving(true);
    try {
      const payload: any = {
        ...company.raw,
      };

      payload[company.isFp ? "referenceNumber" : "accountNumber"] =
        newAccNumber;

      if (company.isFp) {
        payload.notes = coveringNotes;
      }

      if (company.isFp) {
        await httpPut<CustomerSharingResponse>(
          sharePageEndpoints.createFreepostSharing(),
          payload
        );
      } else {
        await httpPut<CustomerSharingResponse>(
          sharePageEndpoints.createCustomerSharing(),
          payload
        );
      }

      const newObj = {
        ...company,
        reference: newAccNumber,
        raw: { ...company.raw },
      };

      if (coveringNotes.length > 0) {
        newObj.raw.notes = coveringNotes;
      }

      // update payment offer if needed
      if (Object.keys(paymentOfferRecord).length != 0) {
        const payload = {
          ...paymentOfferRecord,
          paymentOfferAmount: paymentOfferRecord.paymentOfferAmount
            ? Number(paymentOfferRecord.paymentOfferAmount)
            : null,
        };

        await DebtsService.updatePaymentOffer(paymentOfferRecord.id, payload, {
          skipErrorCapture: true,
        });

        const { paymentOfferAmount, paymentPreferredFrequency } =
          paymentOfferRecord;

        newObj.paymentOfferData = {
          paymentOfferAmount: paymentOfferAmount
            ? Number(paymentOfferAmount)
            : null,
          paymentPreferredFrequency: ordinal(
            parseInt(paymentPreferredFrequency || "0")
          ),
        };
      }

      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? newObj : c))
      );

      onClose();
    } catch (error) {
      console.error("Error updating company:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...(isMobile && {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            maxHeight: "90vh",
            borderRadius: "16px 16px 0 0",
            position: "fixed",
            bottom: 0,
          }),
          ...(!isMobile && {
            maxHeight: "100vh",
            padding: "0px 0px 0px 0px",
          }),
        },
      }}
      TransitionComponent={Transition as any}
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Dialog Title */}
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e0e0e0",
            px: 4,
            justifyContent: "space-between",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Edit Company
          </Typography>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "#9ca3af",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: 2, flexGrow: 1, overflowY: "auto" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0px",
                pt: "16.152px",
                pb: "20px",
                px: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 500, mb: 0, color: ThemeColours.label }}
                >
                  Company name
                </Typography>
                <TextField value={company.name} disabled fullWidth />
              </Box>

              <SpecialTextField
                isAccountField={true}
                label={editReferenceLabel}
                placeholder="Enter your account number"
                noValueChecked={noReferenceId}
                onNoValueChange={(checked) => {
                  setNoReferenceId(checked);
                }}
                value={editedAccountNumber}
                onChange={(e) => setEditedAccountNumber(e.target.value)}
                allowNoValue={true}
                noValueCheckboxLabel={editNoValueCheckboxLabel}
                noValueFieldName="noReferenceId"
                helpLinkText={editReferenceHelpLinkText}
                helpDialog={editReferenceHelpDialog}
                showErrorBelow={true}
              />

              {company.isFp &&
                !(company.raw?.lastSharedDate || company.raw?.submitDate) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 500, mb: 0, color: ThemeColours.label }}
                    >
                      Covering note
                    </Typography>
                    <TextField
                      value={coveringNotes}
                      onKeyDown={(e) => {
                        if (
                          coveringNotes.length >= 500 &&
                          e.key.length === 1 &&
                          !e.ctrlKey &&
                          !e.metaKey
                        ) {
                          showAlert({
                            severity: "warning",
                            message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                          });
                        }
                      }}
                      onChange={(e) => setCoveringNotes(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                      InputProps={{
                        style: {
                          backgroundColor: "white",
                        },
                      }}
                      inputProps={{
                        maxLength: 500,
                        onPaste: (e) => {
                          const pastedText = e.clipboardData.getData("text");
                          const selectionStart = (e.target as any).selectionStart || 0;
                          const selectionEnd = (e.target as any).selectionEnd || 0;
                          if (
                            coveringNotes.length -
                              (selectionEnd - selectionStart) +
                              pastedText.length >
                            500
                          ) {
                            showAlert({
                              severity: "warning",
                              message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                            });
                          }
                        },
                      }}
                    />
                  </Box>
                )}

              {Object.keys(paymentOfferRecord).length != 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 500, mb: 0.8, color: ThemeColours.label }}
                  >
                    I would like to make a payment offer of
                  </Typography>
                  <TextField
                    fullWidth
                    value={paymentOfferRecord.paymentOfferAmount ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                        setPaymentOfferRecord({
                          ...paymentOfferRecord,
                          paymentOfferAmount: val === "" ? null : (val as any),
                        });
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        maxHeight: 45,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">£</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">/month</InputAdornment>
                      ),
                      style: {
                        backgroundColor: "white",
                      },
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      mb: 0.8,
                      color: ThemeColours.label,
                      fontSize: 16,
                    }}
                  >
                    Preferred payment day
                  </Typography>
                  <Select
                    sx={{ backgroundColor: "#fff", mb: 1.5, maxHeight: 45 }}
                    fullWidth
                    value={paymentOfferRecord.paymentPreferredFrequency ?? null}
                    onChange={(e) =>
                      setPaymentOfferRecord({
                        ...paymentOfferRecord,
                        paymentPreferredFrequency: e.target.value as string,
                      })
                    }
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: !isMobile ? "flex-end" : "space-between",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onDelete}
                  sx={{
                    height: "45px",
                    borderRadius: "10px",
                    color: "#FF0F17",
                    fontSize: "18px",
                    fontWeight: 600,
                    textTransform: "none",
                    border: "1px solid #d5d5d5",
                    "&:hover": {
                      backgroundColor: "#e5e7eb",
                      border: "none",
                    },
                    "&:disabled": {
                      backgroundColor: "#f3f4f6",
                      color: "#9ca3af",
                    },
                    minWidth: !isMobile ? 200 : "auto",
                    flex: isMobile ? 1 : "unset",
                  }}
                >
                  Delete company
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    height: "45px",
                    backgroundColor: "#D4E03B",
                    borderRadius: "10px",
                    color: "#003838",
                    fontSize: "18px",
                    fontWeight: 600,
                    textTransform: "none",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "#e6f332ff",
                      border: "none",
                    },
                    "&:disabled": {
                      backgroundColor: "#d5e03b64",
                      color: "#0038384f",
                    },
                    minWidth: !isMobile ? 200 : "auto",
                    flex: isMobile ? 1 : "unset",
                  }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default EditCompanyDialog;
